import React from 'react';
import { useLanguage, Language } from '../contexts/LanguageContext';

// Página de Accesibilidad (2026-10-20). Antes el enlace del pie apuntaba a "#".
// Texto honesto: describe lo que el sitio SÍ tiene hoy y cómo pedir ayuda por correo.
// No promete cumplimiento total de WCAG ni ayuda por teléfono (el teléfono todavía no está activo).
// EN/ES = redacción base. HT/VI = mejor esfuerzo, pendiente de revisión por hablante nativo.
type Block = {
  title: string;
  updated: string;
  intro: string;
  featuresHeading: string;
  features: string[];
  helpHeading: string;
  help: string;
  emailLabel: string;
  effortHeading: string;
  effort: string;
};
const EMAIL = 'info@noorpassportservices.com';
const T: Record<Language, Block> = {
  es: {
    title: 'Accesibilidad',
    updated: 'Última actualización: octubre de 2026',
    intro: 'En Noor queremos que todas las personas, incluidas las personas con alguna discapacidad, puedan usar nuestro sitio y nuestro servicio sin barreras.',
    featuresHeading: 'Lo que ya tiene el sitio',
    features: ['Se puede recorrer solo con el teclado, y hay un enlace "Saltar al contenido principal" al comienzo de cada página.', 'El texto se puede agrandar con el zoom del navegador, y el sitio se adapta al teléfono, la tableta y la computadora.', 'Está disponible en español, inglés, kreyòl y vietnamita.', 'Los íconos y enlaces importantes tienen etiquetas para lectores de pantalla.', 'Los formularios que preparamos usan lenguaje sencillo, paso a paso.'],
    helpHeading: '¿Necesita ayuda o tiene una dificultad?',
    help: 'Si algo del sitio o de los formularios no le funciona, o necesita la información de otra manera (por ejemplo, en letra más grande o explicada con más detalle), escríbanos y cuéntenos qué necesita. Haremos lo posible por ayudarle a completar su trámite.',
    emailLabel: 'Escríbanos a',
    effortHeading: 'Seguimos mejorando',
    effort: 'Trabajamos para seguir las pautas de accesibilidad web WCAG 2.1 (nivel AA). Es posible que alguna parte del sitio todavía no sea totalmente accesible; si encuentra un problema, avísenos por correo y lo revisaremos.'
  },
  en: {
    title: 'Accessibility',
    updated: 'Last updated: October 2026',
    intro: 'At Noor we want everyone, including people with disabilities, to be able to use our website and our service without barriers.',
    featuresHeading: 'What the site already offers',
    features: ['It can be used with a keyboard alone, and there is a "Skip to main content" link at the start of every page.', 'Text can be enlarged with your browser zoom, and the site adapts to phones, tablets and computers.', 'It is available in English, Spanish, Haitian Creole and Vietnamese.', 'Important icons and links have labels for screen readers.', 'The forms we prepare use plain, step-by-step language.'],
    helpHeading: 'Need help or having trouble?',
    help: 'If something on the site or in the forms does not work for you, or you need the information in another way (for example, in larger print or explained in more detail), write to us and tell us what you need. We will do our best to help you complete your paperwork.',
    emailLabel: 'Email us at',
    effortHeading: 'We keep improving',
    effort: 'We work to follow the Web Content Accessibility Guidelines (WCAG) 2.1, level AA. Some parts of the site may not yet be fully accessible; if you find a problem, email us and we will look into it.'
  },
  ht: {
    title: 'Aksesibilite',
    updated: 'Dènye mizajou: oktòb 2026',
    intro: 'Nan Noor, nou vle tout moun, ansanm ak moun ki gen yon andikap, ka itilize sit wèb nou an ak sèvis nou an san baryè.',
    featuresHeading: 'Sa sit la deja genyen',
    features: ['Ou ka sèvi ak li ak klavye a sèlman, epi gen yon lyen "Ale dirèkteman nan kontni prensipal la" nan kòmansman chak paj.', 'Ou ka fè tèks la pi gwo ak zoom navigatè a, epi sit la adapte l ak telefòn, tablèt ak òdinatè.', 'Li disponib an Panyòl, Anglè, Kreyòl ak Vyetnamyen.', 'Ikòn ak lyen enpòtan yo gen etikèt pou lektè ekran.', 'Fòm nou prepare yo itilize lang senp, etap pa etap.'],
    helpHeading: 'Ou bezwen èd oswa ou gen difikilte?',
    help: 'Si yon bagay sou sit la oswa nan fòm yo pa mache pou ou, oswa si ou bezwen enfòmasyon an yon lòt fason (pa egzanp, an gwo lèt oswa eksplike pi detaye), ekri nou epi di nou sa ou bezwen. N ap fè tout sa nou kapab pou ede ou ranpli dosye ou.',
    emailLabel: 'Ekri nou nan',
    effortHeading: 'N ap kontinye amelyore',
    effort: 'Nou ap travay pou swiv direktiv aksesibilite entènèt WCAG 2.1 (nivo AA). Gen kèk pati nan sit la ki ka poko totalman aksesib; si ou jwenn yon pwoblèm, ekri nou pa imèl epi n ap gade li.'
  },
  vi: {
    title: 'Khả năng tiếp cận',
    updated: 'Cập nhật lần cuối: tháng 10 năm 2026',
    intro: 'Tại Noor, chúng tôi muốn mọi người, kể cả người khuyết tật, đều có thể sử dụng trang web và dịch vụ của chúng tôi mà không gặp rào cản.',
    featuresHeading: 'Những gì trang web đã có',
    features: ['Có thể sử dụng chỉ bằng bàn phím, và có liên kết "Chuyển đến nội dung chính" ở đầu mỗi trang.', 'Có thể phóng to chữ bằng chức năng thu phóng của trình duyệt, và trang web phù hợp với điện thoại, máy tính bảng và máy tính.', 'Có bằng tiếng Việt, tiếng Anh, tiếng Tây Ban Nha và tiếng Creole Haiti.', 'Các biểu tượng và liên kết quan trọng có nhãn dành cho trình đọc màn hình.', 'Các mẫu đơn chúng tôi chuẩn bị dùng ngôn ngữ đơn giản, từng bước một.'],
    helpHeading: 'Quý vị cần giúp đỡ hoặc gặp khó khăn?',
    help: 'Nếu có điều gì trên trang web hoặc trong các mẫu đơn không dùng được, hoặc quý vị cần thông tin theo cách khác (ví dụ chữ lớn hơn hoặc giải thích chi tiết hơn), xin hãy viết cho chúng tôi và cho biết quý vị cần gì. Chúng tôi sẽ cố gắng hết sức để giúp quý vị hoàn tất hồ sơ.',
    emailLabel: 'Gửi email cho chúng tôi tại',
    effortHeading: 'Chúng tôi tiếp tục cải thiện',
    effort: 'Chúng tôi nỗ lực tuân theo Hướng dẫn về Khả năng tiếp cận Nội dung Web (WCAG) 2.1, mức AA. Một số phần của trang web có thể chưa hoàn toàn dễ tiếp cận; nếu quý vị gặp vấn đề, xin gửi email cho chúng tôi và chúng tôi sẽ xem xét.'
  }
};
export function AccessibilityPage() {
  const {
    language
  } = useLanguage();
  const t = T[language];
  return <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16" data-id="element-487" data-mp="3fv7gabrwmq2k">
      <h1 className="text-3xl sm:text-4xl font-bold text-brand-textPrimary mb-2" data-id="element-488" data-mp="3kreokb7rwdno">
        {t.title}
      </h1>
      <p className="text-sm text-brand-textSecondary mb-8" data-id="element-489" data-mp="362rz3gofm5iq">{t.updated}</p>
      <p className="text-lg text-brand-textSecondary mb-10 leading-relaxed" data-id="element-490" data-mp="3b0tr4r3u5f6c">
        {t.intro}
      </p>

      <h2 className="text-xl font-semibold text-brand-textPrimary mb-3" data-id="element-491" data-mp="2ljkd5p03ls8">
        {t.featuresHeading}
      </h2>
      <ul className="list-disc pl-6 space-y-2 text-brand-textSecondary leading-relaxed mb-10" data-id="element-492" data-mp="7eclljl9gbzk">
        {t.features.map(f => <li key={f} data-id="element-493" data-mp="1n4dhk3x0zg2o">{f}</li>)}
      </ul>

      <h2 className="text-xl font-semibold text-brand-textPrimary mb-3" data-id="element-494" data-mp="1n3klpxzluazb">
        {t.helpHeading}
      </h2>
      <p className="text-brand-textSecondary leading-relaxed mb-3" data-id="element-495" data-mp="21sqizgevtr4k">{t.help}</p>
      <p className="text-brand-textPrimary mb-10" data-id="element-496" data-mp="1wv883khb11tx">
        {t.emailLabel}{' '}
        <a href={`mailto:${EMAIL}`} className="underline font-medium text-brand-textPrimary" data-id="element-497" data-mp="1942rv47gc7j3">
          {EMAIL}
        </a>
      </p>

      <h2 className="text-xl font-semibold text-brand-textPrimary mb-3" data-id="element-498" data-mp="147uhe44792i9">
        {t.effortHeading}
      </h2>
      <p className="text-brand-textSecondary leading-relaxed" data-id="element-499" data-mp="1e3p4pcjdblrd">{t.effort}</p>
    </div>;
}